import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import {
  CardGiftcardOutlined,
  CheckCircleOutlined,
  TokenOutlined,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import Header from "../components/Header";
import FooterComp from "../components/FooterComp";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import {
  formatPlanPriceInDisplay,
  resolvePlanDisplayCurrency,
} from "../../utils/planDisplayCurrency";

function isPaidPlan(plan) {
  const n = parseFloat(String(plan?.price ?? "0"));
  return Number.isFinite(n) && n > 0;
}

function getPaystackCtor() {
  if (typeof window !== "undefined") {
    if (typeof window.PaystackPop === "function") return window.PaystackPop;
    if (window.PaystackPop && typeof window.PaystackPop.default === "function") {
      return window.PaystackPop.default;
    }
  }
  return null;
}

const GiftCredits = () => {
  const theme = useTheme();
  const { hitAxios, showSnack } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);

  const [web, setWeb] = React.useState({});
  const [plans, setPlans] = React.useState([]);
  const [planDisplay, setPlanDisplay] = React.useState(() =>
    resolvePlanDisplayCurrency({}),
  );
  const [paystackGw, setPaystackGw] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  const [recipientEmail, setRecipientEmail] = React.useState("");
  const [payerEmail, setPayerEmail] = React.useState("");
  const [payerTouched, setPayerTouched] = React.useState(false);
  const [lookup, setLookup] = React.useState(null);
  const [lookingUp, setLookingUp] = React.useState(false);
  const [selectedPlanId, setSelectedPlanId] = React.useState(null);
  const [paying, setPaying] = React.useState(false);
  const [result, setResult] = React.useState(null);

  const currency = planDisplay?.currency || { symbol: "₦", code: "NGN", rate: 1550 };
  const formatGiftPrice = React.useCallback(
    (usdAmount) =>
      `${currency.symbol || ""}${formatPlanPriceInDisplay(usdAmount, currency)}${
        currency.code ? ` ${currency.code}` : ""
      }`,
    [currency],
  );

  React.useEffect(() => {
    const blockContextMenu = (e) => e.preventDefault();
    const blockDrag = (e) => e.preventDefault();
    document.addEventListener("contextmenu", blockContextMenu);
    document.addEventListener("dragstart", blockDrag);
    return () => {
      document.removeEventListener("contextmenu", blockContextMenu);
      document.removeEventListener("dragstart", blockDrag);
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [wRes, pRes, gwRes] = await Promise.all([
          hitAxios({
            path: "/api/web/get_web_public",
            post: false,
            admin: false,
            showLoading: false,
            showSnackbar: false,
          }),
          hitAxios({
            path: "/api/plan/get_all",
            post: false,
            admin: false,
            showLoading: false,
            showSnackbar: false,
          }),
          hitAxios({
            path: "/api/payment/active-gateways",
            post: false,
            admin: false,
            showLoading: false,
            showSnackbar: false,
          }),
        ]);
        if (cancelled) return;
        if (wRes?.data?.success) setWeb(wRes.data.data || {});
        if (pRes?.data?.success) {
          const raw = Array.isArray(pRes.data.plans) ? pRes.data.plans : [];
          setPlans(raw.filter(isPaidPlan));
        }
        if (gwRes?.data?.success) {
          const gwData = gwRes.data.data;
          // Gift checkout is Paystack-only — show the same settlement currency Paystack will charge.
          const ps = gwData?.paystack;
          if (ps?.active && ps?.settlement?.code) {
            const st = ps.settlement;
            const rate = Number(st.rate);
            setPlanDisplay({
              currency: {
                symbol: st.symbol || (String(st.code).toUpperCase() === "NGN" ? "₦" : "$"),
                code: String(st.code || "NGN").toUpperCase(),
                rate: rate > 0 ? rate : String(st.code).toUpperCase() === "NGN" ? 1550 : 1,
              },
              isSinglePaystackNgn: String(st.code).toUpperCase() === "NGN",
            });
          } else {
            setPlanDisplay(resolvePlanDisplayCurrency(gwData));
          }
          setPaystackGw(ps ?? null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [hitAxios]);

  const selectedPlan = plans.find((p) => String(p.id) === String(selectedPlanId));

  const runLookup = async () => {
    setLookingUp(true);
    setLookup(null);
    setResult(null);
    const email = recipientEmail.trim().toLowerCase();
    try {
      const res = await hitAxios({
        path: "/api/payment/gift/lookup",
        post: true,
        admin: false,
        obj: { email },
        showLoading: false,
        showSnackbar: false,
      });
      if (res?.data?.success) {
        const data = res.data.data || {};
        setLookup(data);
        if (data.exists) {
          // Prefill receipt email from recipient; user can still change it.
          if (!payerTouched || !payerEmail.trim()) {
            setPayerEmail(email);
            setPayerTouched(false);
          }
        } else {
          showSnack(
            res.data.msg ||
              "No account found for that email. Ask them to sign up first.",
            false,
          );
        }
      } else {
        showSnack(res?.data?.msg || "Could not look up that email", false);
      }
    } finally {
      setLookingUp(false);
    }
  };

  const recipientDisplayName = React.useMemo(() => {
    if (!lookup?.exists) return "";
    return String(lookup.name || "").trim();
  }, [lookup]);

  const handlePay = async () => {
    if (!selectedPlan) {
      showSnack(lang?.giftPickPlan || "Choose a credit package", false);
      return;
    }
    if (!lookup?.exists) {
      showSnack(
        lang?.giftLookupFirst || "Confirm the recipient account first",
        false,
      );
      return;
    }
    if (!payerEmail.trim()) {
      showSnack(lang?.giftNeedPayer || "Enter your email for the payment receipt", false);
      return;
    }
    if (!paystackGw?.active) {
      showSnack(
        lang?.giftPaystackOff || "Online payment is not available right now",
        false,
      );
      return;
    }

    setPaying(true);
    setResult(null);
    try {
      const res = await hitAxios({
        path: "/api/payment/gift/paystack/create",
        post: true,
        admin: false,
        obj: {
          plan_id: selectedPlan.id,
          recipient_email: recipientEmail.trim(),
          payer_email: payerEmail.trim(),
        },
        showLoading: false,
        showSnackbar: false,
      });
      if (!res?.data?.success) {
        showSnack(res?.data?.msg || "Could not start payment", false);
        setPaying(false);
        return;
      }

      let d = res.data || {};
      if (d.data && typeof d.data === "object" && !Array.isArray(d.data)) {
        d = { ...d, ...d.data };
      }

      let PaystackCtor = getPaystackCtor();
      if (!PaystackCtor) {
        try {
          const mod = await import("@paystack/inline-js");
          PaystackCtor =
            typeof mod?.default === "function"
              ? mod.default
              : typeof mod?.PaystackPop === "function"
                ? mod.PaystackPop
                : typeof mod === "function"
                  ? mod
                  : null;
        } catch {
          PaystackCtor = null;
        }
      }
      if (typeof PaystackCtor !== "function") {
        showSnack("Payment could not open. Please try again.", false);
        setPaying(false);
        return;
      }

      const onSuccess = async (transaction) => {
        const ref = transaction?.reference || transaction?.trxref;
        if (!ref) {
          setPaying(false);
          return;
        }
        try {
          const vRes = await hitAxios({
            path: "/api/payment/gift/paystack/verify",
            post: true,
            admin: false,
            obj: { reference: ref },
            showLoading: false,
            showSnackbar: false,
          });
          if (vRes?.data?.success) {
            setResult({
              success: true,
              msg:
                vRes.data.msg ||
                `Credits delivered to ${recipientEmail.trim()}`,
            });
          } else {
            setResult({
              success: false,
              msg: vRes?.data?.msg || "Verification failed. Contact support with your receipt.",
            });
          }
        } catch {
          setResult({
            success: false,
            msg: "Verification failed. Contact support with your receipt.",
          });
        } finally {
          setPaying(false);
        }
      };

      const popup = new PaystackCtor();
      const accessCode = d.access_code ?? d.accessCode;
      if (accessCode) {
        popup.resumeTransaction(String(accessCode), {
          onSuccess,
          onCancel: () => setPaying(false),
          onError: () => {
            setPaying(false);
            showSnack("Payment could not complete", false);
          },
        });
        return;
      }

      const amountNum = Number(d.amount);
      popup.newTransaction({
        key: d.publicKey || paystackGw?.publicKey,
        email: d.email || payerEmail.trim(),
        amount: amountNum,
        currency: d.currency || paystackGw?.settlement?.code || "NGN",
        reference: d.reference,
        metadata: d.metadata,
        onSuccess,
        onCancel: () => setPaying(false),
        onError: () => {
          setPaying(false);
          showSnack("Payment could not complete", false);
        },
      });
    } catch (e) {
      setPaying(false);
      showSnack(e?.message || "Payment failed", false);
    }
  };

  return (
    <Box
      onContextMenu={(e) => e.preventDefault()}
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      <Header />
      <Box sx={{ height: { xs: 56, md: 64 } }} />

      <Box
        sx={{
          flex: 1,
          maxWidth: 720,
          width: "100%",
          mx: "auto",
          px: { xs: 2, sm: 3 },
          py: { xs: 3, md: 5 },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
          <CardGiftcardOutlined color="primary" sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h4" fontWeight={800} letterSpacing="-0.02em">
              {lang?.giftTitle || "Gift credits"}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {lang?.giftSubtitle ||
                "Top up someone else's Beyund AI account — no login required."}
            </Typography>
          </Box>
        </Stack>

        {result && (
          <Alert
            severity={result.success ? "success" : "error"}
            icon={result.success ? <CheckCircleOutlined /> : undefined}
            sx={{ my: 2, borderRadius: 2 }}
            onClose={() => setResult(null)}
          >
            {result.msg}
          </Alert>
        )}

        <Card
          elevation={0}
          sx={{
            mt: 3,
            p: 2.5,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            1. Who receives the credits?
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              fullWidth
              size="small"
              type="email"
              label={lang?.giftRecipientEmail || "Recipient email"}
              placeholder="friend@example.com"
              value={recipientEmail}
              onChange={(e) => {
                setRecipientEmail(e.target.value);
                setLookup(null);
                if (!payerTouched) {
                  setPayerEmail(e.target.value.trim());
                }
              }}
            />
            <LoadingButton
              variant="outlined"
              loading={lookingUp}
              onClick={runLookup}
              sx={{ textTransform: "none", fontWeight: 700, whiteSpace: "nowrap" }}
            >
              {lang?.giftConfirm || "Confirm account"}
            </LoadingButton>
          </Stack>
          {lookup?.exists && (
            <Alert severity="success" sx={{ mt: 1.5 }}>
              <Typography variant="body2" fontWeight={700} component="span">
                Account found
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Name:{" "}
                <strong>
                  {recipientDisplayName || "No name set on this account"}
                </strong>
              </Typography>
              <Typography variant="body2">
                Email:{" "}
                <strong>{lookup.email || recipientEmail.trim()}</strong>
              </Typography>
            </Alert>
          )}
          {lookup && lookup.exists === false && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              No account for that email. They need to{" "}
              <a href="/user/signup">sign up</a> first.
            </Alert>
          )}
        </Card>

        <Card
          elevation={0}
          sx={{
            mt: 2,
            p: 2.5,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            2. Your email (for payment receipt)
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="email"
            label={lang?.giftPayerEmail || "Your email"}
            value={payerEmail}
            onChange={(e) => {
              setPayerTouched(true);
              setPayerEmail(e.target.value);
            }}
            helperText="Used for the payment receipt — change if you’re paying for someone else"
          />
        </Card>

        <Box mt={2}>
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            3. Choose a credit package
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : plans.length === 0 ? (
            <Alert severity="info">No paid packages available right now.</Alert>
          ) : (
            <Stack spacing={1.5}>
              {plans.map((plan) => {
                const selected = String(plan.id) === String(selectedPlanId);
                const credits = Number(plan.credits || 0);
                return (
                  <Card
                    key={plan.id}
                    elevation={0}
                    sx={{
                      border: `2px solid ${
                        selected
                          ? theme.palette.primary.main
                          : theme.palette.divider
                      }`,
                      borderRadius: 2,
                      bgcolor: selected
                        ? alpha(theme.palette.primary.main, 0.04)
                        : "background.paper",
                    }}
                  >
                    <CardActionArea onClick={() => setSelectedPlanId(plan.id)}>
                      <CardContent sx={{ py: 2 }}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Box>
                            <Typography fontWeight={800}>{plan.title}</Typography>
                            <Chip
                              size="small"
                              icon={<TokenOutlined sx={{ fontSize: "14px !important" }} />}
                              label={`${credits.toLocaleString()} credits`}
                              sx={{ mt: 0.75, fontWeight: 700 }}
                            />
                          </Box>
                          <Typography variant="h6" fontWeight={800}>
                            {formatGiftPrice(plan.price)}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                );
              })}
            </Stack>
          )}
        </Box>

        <LoadingButton
          fullWidth
          size="large"
          variant="contained"
          loading={paying}
          disabled={!lookup?.exists || !selectedPlan}
          onClick={handlePay}
          sx={{ mt: 3, py: 1.5, textTransform: "none", fontWeight: 800 }}
        >
          {selectedPlan
            ? `${lang?.giftPayCta || "Pay"} ${formatGiftPrice(selectedPlan.price)} →`
            : lang?.giftPayCta || "Pay & gift credits"}
        </LoadingButton>

        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          textAlign="center"
          mt={2}
        >
          Secure checkout via Paystack
          {currency?.code ? ` (${currency.code})` : ""}. Credits are added to the
          recipient as soon as payment succeeds.
        </Typography>

        <Button
          href="/user/login"
          sx={{ mt: 2, display: "block", mx: "auto", textTransform: "none" }}
        >
          Already have an account? Transfer your own credits from Billing →
        </Button>
      </Box>

      <FooterComp web={web} />
    </Box>
  );
};

export default GiftCredits;
